import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Building2, 
  Heart, 
  GraduationCap, 
  Check,
  Globe,
  MessageCircle
} from 'lucide-react';

const PartnerPage = () => {
  const partnerTypes = [
    {
      icon: <Building2 className="w-7 h-7" />,
      title: 'Healthcare Organizations',
      description: 'Hospitals, clinics, rehabilitation centers, and healthcare providers interested in assistive technology solutions for patients.',
      color: 'blue'
    },
    {
      icon: <Heart className="w-7 h-7" />,
      title: 'Accessibility Advocates',
      description: 'Non-profits, advocacy groups, and organizations dedicated to improving accessibility and independence for the visually impaired.',
      color: 'red'
    },
    {
      icon: <GraduationCap className="w-7 h-7" />,
      title: 'Research Institutions',
      description: 'Universities, research labs, and academic institutions exploring assistive technology, human-computer interaction, or accessibility.',
      color: 'purple'
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: 'Distribution Partners',
      description: 'Organizations that can help bring ClearWay to communities that need it most, including international accessibility initiatives.',
      color: 'green'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    green: 'bg-green-500/10 text-green-400 border-green-500/20'
  };

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Partnerships</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Partner
              <br />
              <span className="text-gray-500">With Us</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We believe collaboration amplifies impact. Join us in making mobility 
              technology more accessible to those who need it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Who We Partner With</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              ClearWay welcomes partnerships with organizations aligned with our mission 
              of improving accessibility and independence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {partnerTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 hover:border-gray-700 transition-colors"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${colorClasses[type.color]}`}>
                  {type.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{type.title}</h3>
                <p className="text-gray-400 leading-relaxed">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">
                Collaboration Opportunities
              </p>
              <h2 className="text-4xl font-semibold tracking-tight mb-6">
                How We Can Work Together
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Whether you represent a healthcare organization, advocacy group, research 
                institution, or potential distribution partner, we are interested in 
                exploring how ClearWay can support your accessibility initiatives.
              </p>
              <div className="space-y-4">
                {[
                  'Product demonstrations and evaluations',
                  'Pilot programs with target user groups',
                  'Research collaboration and data sharing',
                  'Feedback and co-development opportunities',
                  'Distribution and accessibility partnerships'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Interactive Partnership Visualization */}
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-emerald-500/10 border border-gray-800/50 p-8">
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  <defs>
                    <linearGradient id="partnerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0.1" />
                    </linearGradient>
                  </defs>
                  
                  {/* Central node - ClearWay */}
                  <motion.circle
                    cx="200"
                    cy="200"
                    r="50"
                    fill="#1a1a1a"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <text x="200" y="195" textAnchor="middle" fill="#3b82f6" fontSize="12" fontWeight="600">CLEARWAY</text>
                  <text x="200" y="210" textAnchor="middle" fill="#6b7280" fontSize="10">Student Team</text>
                  
                  {/* Partner nodes */}
                  {[
                    { angle: 0, label: 'Healthcare', color: '#3b82f6' },
                    { angle: 90, label: 'Advocacy', color: '#ef4444' },
                    { angle: 180, label: 'Research', color: '#a855f7' },
                    { angle: 270, label: 'Distribution', color: '#10b981' },
                  ].map((partner, i) => {
                    const radians = (partner.angle - 90) * (Math.PI / 180);
                    const x = 200 + 120 * Math.cos(radians);
                    const y = 200 + 120 * Math.sin(radians);
                    
                    return (
                      <motion.g key={partner.label}>
                        {/* Connection line */}
                        <motion.line
                          x1="200"
                          y1="200"
                          x2={x}
                          y2={y}
                          stroke={partner.color}
                          strokeWidth="1"
                          strokeDasharray="5 5"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 1.5, delay: i * 0.3 }}
                        />
                        
                        {/* Partner node */}
                        <motion.circle
                          cx={x}
                          cy={y}
                          r="35"
                          fill="#1a1a1a"
                          stroke={partner.color}
                          strokeWidth="2"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5, delay: 0.5 + i * 0.2 }}
                        />
                        <text x={x} y={y + 5} textAnchor="middle" fill={partner.color} fontSize="10" fontWeight="500">
                          {partner.label}
                        </text>
                        
                        {/* Animated pulse */}
                        <motion.circle
                          cx={x}
                          cy={y}
                          r="35"
                          fill="none"
                          stroke={partner.color}
                          strokeWidth="1"
                          initial={{ scale: 1, opacity: 0.5 }}
                          animate={{ scale: 1.5, opacity: 0 }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
                        />
                      </motion.g>
                    );
                  })}
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Typebot Chat Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Let&apos;s Connect</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Start a Conversation</h2>
            <p className="text-gray-400">
              Tell us about your organization and how you&apos;d like to partner with ClearWay.
              Our interactive assistant will guide you through the process.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden border border-gray-800/50 bg-gradient-to-b from-gray-800/30 to-gray-900/50"
          >
            <iframe
              src="https://typebot.co/clear-way-site-wa-4q7k7w4"
              style={{ 
                width: "100%", 
                height: "600px",
                border: "none",
                borderRadius: "1.5rem",
                background: "#1a1a1a"
              }}
              title="ClearWay Partnership Assistant"
              allow="microphone"
            />
          </motion.div>

          <p className="text-xs text-gray-600 text-center mt-4">
            Your information is handled securely and only used to facilitate partnership discussions.
          </p>
        </div>
      </section>

      {/* Alternative Contact */}
      <section className="py-16 px-6 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-gray-800/30 border border-gray-800/50"
          >
            <h3 className="text-xl font-semibold mb-2">Prefer email?</h3>
            <p className="text-gray-400 mb-4">
              You can also reach out directly to discuss partnership opportunities.
            </p>
            <p className="text-blue-400 font-medium">partner@clearway-mobility.com</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPage;
